import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { logementAPI } from '../../service/api';
import { FiMapPin, FiHome, FiArrowLeft, FiGrid, FiExternalLink, FiMap } from 'react-icons/fi';
import { resolveUploadUrl } from '../../config/config';
import useAutoRefresh from '../../hooks/useAutoRefresh';

const typeLabels = { studio: 'Studio', appartement: 'Appartement', maison: 'Maison', chambre: 'Chambre' };

function extractCoordsFromIframe(iframeUrl) {
  if (!iframeUrl) return null;
  const srcMatch = iframeUrl.match(/src=["']([^"']+)["']/);
  const url = srcMatch ? srcMatch[1] : iframeUrl;
  const llMatch = url.match(/ll=([-\d.]+),([-\d.]+)/);
  if (llMatch) return { lat: llMatch[1], lng: llMatch[2] };
  const qMatch = url.match(/q=([-\d.]+),([-\d.]+)/);
  if (qMatch) return { lat: qMatch[1], lng: qMatch[2] };
  const placeMatch = url.match(/!3d([-\d.]+)!4d([-\d.]+)/);
  if (placeMatch) return { lat: placeMatch[1], lng: placeMatch[2] };
  return null;
}

export default function LogementMap() {
  const [logements, setLogements] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLogements = async (skipLoading = false) => {
    if (!skipLoading) setLoading(true);
    try {
      const res = await logementAPI.getAll({ limit: 100 });
      setLogements(res.data.properties || []);
    } catch (err) { console.error(err); }
    finally { if (!skipLoading) setLoading(false); }
  };

  useEffect(() => { fetchLogements(); }, []);
  useAutoRefresh(() => fetchLogements(true), 30000);

  const coordsList = logements
    .map(p => extractCoordsFromIframe(p.mapsEmbedUrl))
    .filter(Boolean);

  const mapSrc = coordsList.length > 0
    ? `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${coordsList[0].lat},${coordsList[0].lng}&zoom=12`
    : 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d127045.98743380566!2d25.416667!3d-10.716667!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1975638a3f0b0b0b%3A0x3b0b0b0b0b0b0b0b!2sKolwezi%2C%20Democratic%20Republic%20of%20the%20Congo!5e0!3m2!1sen!2s!4v1';

  return (
    <div className="page-container py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to="/logements" className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider mb-2"
            style={{ color: '#7A828A' }}>
            <FiArrowLeft /> Retour à la liste
          </Link>
          <h1 className="font-serif text-3xl font-bold tracking-tight" style={{ color: '#0A192F' }}>Carte interactive</h1>
          <p className="text-sm mt-1" style={{ color: '#7A828A' }}>{logements.length} logement(s) trouvé(s)</p>
        </div>
        <Link to="/logements" className="btn-outline-root flex items-center gap-2">
          <FiGrid /> Vue liste
        </Link>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-2" style={{ borderColor: '#C59B6C', borderTopColor: 'transparent' }} />
        </div>
      ) : logements.length === 0 ? (
        <div className="card-root p-12 text-center">
          <FiHome className="w-16 h-16 mx-auto mb-4" style={{ color: '#E5E5E0' }} />
          <p className="text-sm" style={{ color: '#7A828A' }}>Aucun logement disponible</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="card-root overflow-hidden" style={{ height: '60vh', minHeight: '400px' }}>
              <iframe
                title="Carte des logements"
                src={mapSrc}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <p className="text-xs mt-2 flex items-center gap-1" style={{ color: '#7A828A' }}>
              <FiMap className="w-3 h-3" /> Carte centrée sur Kolwezi — les marqueurs Google Maps ne sont pas disponibles sans clé API.
            </p>
          </div>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <p className="text-[11px] font-bold tracking-widest uppercase" style={{ color: '#7A828A' }}>
              Logements ({logements.length})
            </p>
            {logements.map((p) => {
              const hasCoords = extractCoordsFromIframe(p.mapsEmbedUrl);
              return (
                <Link key={p.id} to={`/logements/${p.id}`} className="card-root p-4 flex gap-3 hover:shadow-md transition-shadow">
                  <div className="w-16 h-16 flex-shrink-0 flex items-center justify-center overflow-hidden" style={{ backgroundColor: '#E5E5E0' }}>
                    {p.images && p.images.length > 0 ? (
                      <img src={resolveUploadUrl(p.images[0])} alt={p.titre} className="w-full h-full object-cover" />
                    ) : (
                      <FiHome className="w-6 h-6" style={{ color: '#88929B' }} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold truncate" style={{ color: '#0A192F' }}>{p.titre}</h3>
                    <p className="text-xs truncate flex items-center gap-1 mt-0.5" style={{ color: '#7A828A' }}>
                      <FiMapPin className="w-3 h-3 flex-shrink-0" /> {p.adresse}
                    </p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs font-bold" style={{ color: '#C59B6C' }}>{Number(p.prix).toLocaleString()} $</span>
                      <span className="text-[10px] font-bold tracking-widest uppercase px-1.5 py-0.5 border" style={{ color: '#0A192F', borderColor: '#0A192F' }}>
                        {typeLabels[p.type] || p.type}
                      </span>
                    </div>
                    {hasCoords && (
                      <a href={`https://www.google.com/maps?q=${hasCoords.lat},${hasCoords.lng}`} target="_blank" rel="noopener noreferrer"
                        className="text-[10px] flex items-center gap-1 mt-1" style={{ color: '#C59B6C' }}
                        onClick={(e) => e.stopPropagation()}>
                        <FiExternalLink className="w-3 h-3" /> Ouvrir dans Google Maps
                      </a>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
